import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { Lender, LenderProduct } from '../../api/lenders';
import { useProducts } from '../../hooks/useProducts';
import { useRemoveLenderProduct } from '../../hooks/useLenders';
import SuccessToast from '../../components/common/SuccessToast';
import LenderProductRemoveDialog from './LenderProductRemoveDialog';
import LenderProductEditDrawer from './LenderProductEditDrawer';
import searchIcon from './assets/product-search.svg';
import plusIcon from './assets/product-plus.svg';
import exportIcon from './assets/product-export.svg';
import filterIcon from './assets/product-filter.svg';
import sortIcon from './assets/product-sort.svg';
import dotsIcon from './assets/product-dots.svg';
import previousIcon from './assets/product-chevron-left.svg';
import nextIcon from './assets/product-chevron-right.svg';

interface ProductRow {
  assignment: LenderProduct;
  service: string;
  category: string;
  provider: string;
}

type SortKey = 'service' | 'category' | 'product' | 'provider';
const PAGE_SIZE = 10;

export default function LenderProductsTab({ lender }: { lender: Lender }) {
  const { data: catalog, isLoading, isError } = useProducts();
  const removal = useRemoveLenderProduct();
  const [removing, setRemoving] = useState<LenderProduct | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);

  const actionRef = useRef<HTMLButtonElement | null>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const editMenuRef = useRef<HTMLButtonElement>(null);
  const submittingRef = useRef(false);
  const returnFocus = useCallback(() => {
    if (actionRef.current?.isConnected) actionRef.current.focus();
    else headingRef.current?.focus();
  }, []);
  const confirmRemoval = async () => {
    if (!removing || submittingRef.current) return;
    submittingRef.current = true;
    try {
      await removal.mutateAsync({ urn: lender.urn, productUrn: removing.urn });
      // The removed row can disappear in a later query notification.
      actionRef.current = null;
      setRemoving(null);
      setShowSuccess(true);
    } catch {
      // Keep the dialog open and the assigned product visible so the user can retry.
    } finally {
      submittingRef.current = false;
    }
  };
  const [editing, setEditing] = useState<LenderProduct | null>(null);
  const [openMenu, setOpenMenu] = useState<{ assignment: LenderProduct; top: number; right: number } | null>(null);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<{ key: SortKey; direction: 1 | -1 } | null>(null);
  const [page, setPage] = useState(0);

  useEffect(() => {
    if (!openMenu) return;
    editMenuRef.current?.focus();
    const close = () => { setOpenMenu(null); returnFocus(); };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };
    window.addEventListener('scroll', close, { capture: true, passive: true });
    window.addEventListener('resize', close);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [openMenu, returnFocus]);

  const rows = useMemo(() => (lender.products ?? []).map((assigned): ProductRow => {
    const assignment = typeof assigned === 'string' ? { name: assigned, urn: assigned } : assigned;
    const product = catalog?.find((item) => item.urn === assignment.urn || item.vendorProducts?.some((vendor) => vendor.urn === assignment.urn));
    const vendor = product?.vendorProducts?.find((item) => item.urn === assignment.urn);
    return {
      assignment,
      service: product?.serviceType ?? '—',
      category: product?.productType ?? '—',
      provider: vendor?.vendorName ?? '—',
    };
  }), [lender.products, catalog]);

  const visibleRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    const filtered = term
      ? rows.filter((row) => [row.service, row.category, row.assignment.name, row.provider]
        .some((value) => value.toLowerCase().includes(term)))
      : rows;
    if (!sort) return filtered;
    const value = (row: ProductRow) => sort.key === 'product' ? row.assignment.name : row[sort.key];
    return [...filtered].sort((a, b) => sort.direction * value(a).localeCompare(value(b)));
  }, [rows, search, sort]);

  const pageCount = Math.max(1, Math.ceil(visibleRows.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount - 1);
  const pagedRows = visibleRows.slice(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE);
  const pageStart = Math.max(1, Math.min(currentPage - 1, pageCount - 4));
  const pageNumbers = Array.from({ length: Math.min(pageCount, 5) }, (_, index) => pageStart + index);
  const showingFrom = visibleRows.length === 0 ? 0 : currentPage * PAGE_SIZE + 1;
  const showingTo = Math.min((currentPage + 1) * PAGE_SIZE, visibleRows.length);

  const changeSort = (key: SortKey) => {
    setSort((current) => ({ key, direction: current?.key === key && current.direction === 1 ? -1 : 1 }));
    setPage(0);
  };

  return (
    <section className="rounded-xl border border-[#ddd] bg-surface p-6 text-content dark:border-border-subtle sm:p-10">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h2 ref={headingRef} tabIndex={-1} className="text-[18px] font-medium leading-[27px]">Products</h2>
        <button type="button" disabled title="Adding products is not available yet" className="flex h-[38px] w-[154px] cursor-not-allowed items-center justify-center gap-2.5 rounded-lg bg-brand text-base font-semibold text-white">
          <img src={plusIcon} alt="" />
          Add Product
        </button>
      </div>

      <div className="mt-8 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <label className="flex h-[43px] w-full max-w-[408px] items-center gap-2 rounded-[5px] border border-border-input bg-surface px-3 focus-within:ring-2 focus-within:ring-brand">
            <img src={searchIcon} alt="" />
            <span className="sr-only">Search lender products</span>
            <input
              type="search"
              value={search}
              onChange={(event) => { setSearch(event.target.value); setPage(0); }}
              placeholder="Search ..."
              className="min-w-0 flex-1 bg-transparent text-sm text-content outline-none placeholder:text-content-secondary"
            />
          </label>
          <div className="flex items-center gap-2">
            <button type="button" disabled title="Export is not available yet" aria-label="Export products" className="cursor-not-allowed rounded-[7px] bg-surface"><img src={exportIcon} alt="" /></button>
            <button type="button" disabled title="Filtering is not available yet" aria-label="Filter products" className="cursor-not-allowed rounded-[7px] bg-surface"><img src={filterIcon} alt="" /></button>
          </div>
        </div>

        {isError && <p role="alert" className="text-sm text-red-600">Product details could not be loaded. Assigned products are still shown.</p>}
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full min-w-[680px] table-fixed border-collapse text-left">
            <colgroup><col /><col /><col /><col /><col className="w-[26px]" /></colgroup>
            <thead className="table-head text-white">
              <tr className="h-[42px]">
                {([['service', 'Service'], ['category', 'Category'], ['product', 'Product'], ['provider', 'Provider']] as [SortKey, string][]).map(([key, label]) => (
                  <th key={key} scope="col" className="px-4 text-xs font-semibold">
                    <button type="button" onClick={() => changeSort(key)} className="inline-flex items-center gap-1.5 whitespace-nowrap">
                      {label}<img src={sortIcon} alt="" />
                    </button>
                  </th>
                ))}
                <th scope="col"><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody className="bg-surface text-[13px] leading-5 text-content-secondary">
              {isLoading ? (
                <tr><td colSpan={5} className="px-4 py-6 text-center">Loading products...</td></tr>
              ) : pagedRows.length === 0 ? (
                <tr><td colSpan={5} className="px-4 py-6 text-center">No products found.</td></tr>
              ) : pagedRows.map((row) => (
                <tr key={row.assignment.urn} className={`h-11 border-b border-gray-200 last:border-b-0 dark:border-border-subtle${openMenu?.assignment.urn === row.assignment.urn ? 'outline outline-2 -outline-offset-2 outline-brand-deep' : ''}`}>
                  <td className="truncate px-4" title={row.service}>{row.service}</td>
                  <td className="truncate px-4" title={row.category}>{row.category}</td>
                  <td className="truncate px-4" title={row.assignment.name}>{row.assignment.name}</td>
                  <td className="truncate px-4" title={row.provider}>{row.provider}</td>
                  <td className="text-center">
                    <button
                      type="button"
                      aria-label={`Actions for ${row.assignment.name}`}
                      aria-expanded={openMenu?.assignment.urn === row.assignment.urn}
                      onClick={(event) => {
                        actionRef.current = event.currentTarget;
                        const rect = event.currentTarget.getBoundingClientRect();
                        setOpenMenu((current) => current?.assignment.urn === row.assignment.urn
                          ? null
                          : { assignment: row.assignment, top: Math.max(8, Math.min(rect.bottom, window.innerHeight - 98)), right: Math.max(8, window.innerWidth - rect.right) });
                      }}
                      className="flex h-7 w-5 items-center justify-center rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand"
                    ><img src={dotsIcon} alt="" className="rotate-90" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap items-end justify-between gap-3 pt-4 text-[13px] text-content-secondary">
          <p>Showing {showingFrom}-{showingTo} of {visibleRows.length} results</p>
          <nav aria-label="Product pages" className="flex items-center gap-2">
            <button type="button" aria-label="Previous page" disabled={currentPage === 0} onClick={() => setPage(currentPage - 1)} className="flex h-8 w-10 items-center justify-center rounded-md border border-border bg-surface disabled:opacity-50"><img src={previousIcon} alt="" /></button>
            {pageNumbers.map((number) => (
              <button key={number} type="button" aria-label={`Page ${number}`} aria-current={number === currentPage + 1 ? 'page' : undefined} onClick={() => setPage(number - 1)} className={`flex h-8 w-8 items-center justify-center rounded-md border text-[13px] ${number === currentPage + 1 ? 'border-brand-deep bg-brand-deep font-semibold text-white' : 'border-border bg-surface font-medium text-content'}`}>{number}</button>
            ))}
            <button type="button" aria-label="Next page" disabled={currentPage + 1 >= pageCount} onClick={() => setPage(currentPage + 1)} className="flex h-8 w-10 items-center justify-center rounded-md border border-border bg-surface disabled:opacity-50"><img src={nextIcon} alt="" /></button>
          </nav>
        </div>
      </div>

      <div className="mt-10 flex justify-end">
        <button type="button" disabled title="Product changes are saved in the edit drawer" className="flex h-11 min-w-[88px] items-center justify-center rounded-lg bg-[#3a149e] px-6 text-[15px] font-semibold text-white opacity-20">Save</button>
      </div>

      {openMenu && createPortal(
        <>
          <button type="button" aria-label="Close product actions" className="fixed inset-0 z-40 cursor-default" onClick={() => { setOpenMenu(null); returnFocus(); }} />
          <div role="group" aria-label={`Product actions for ${openMenu.assignment.name}`} style={{ top: openMenu.top, right: openMenu.right }} className="fixed z-50 w-[105px] rounded-lg border border-[#e8ebf1] bg-white py-0.5 text-[#273141] shadow-[0_4px_4px_rgba(0,0,0,0.08)]">
            <div className="h-[42px] px-1 py-1"><button ref={editMenuRef} type="button" onClick={() => { setEditing(openMenu.assignment); setOpenMenu(null); }} className="h-full w-full rounded border-[1.5px] border-transparent px-2 text-left text-[15px] leading-[22.5px] hover:bg-[#e8ebf1] hover:text-[#0f1b2f] focus-visible:bg-[#e8ebf1] focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand">Edit</button></div>
            <div className="h-[42px] px-1 py-1"><button type="button" onClick={() => { removal.reset(); setShowSuccess(false); setRemoving(openMenu.assignment); setOpenMenu(null); }} className="h-full w-full rounded border-[1.5px] border-transparent px-2 text-left text-[15px] leading-[22.5px] hover:bg-[#e8ebf1] hover:text-[#0f1b2f] focus-visible:bg-[#e8ebf1] focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand">Remove</button></div>
          </div>
        </>, document.body,
      )}
      {removing && <LenderProductRemoveDialog productName={removing.name} lenderName={lender.displayName || lender.name} isPending={removal.isPending} errorMessage={removal.isError ? 'Product could not be removed. Please try again.' : null} onConfirm={confirmRemoval} onCancel={() => { if (!submittingRef.current) setRemoving(null); }} returnFocus={returnFocus} />}
      {showSuccess && <SuccessToast message="Success: Product Removed" onDismiss={() => setShowSuccess(false)} />}
      {editing && <LenderProductEditDrawer key={editing.urn} lender={lender} product={editing} onClose={() => setEditing(null)} catalog={catalog} />}
    </section>
  );
}
